import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const FALLBACK_LOGO = "https://customer-assets-wrfwihn1.emergentagent.net/job_shop-admin-hub-38/artifacts/9jpl3ctg_LOGO%20TIENDA%20WEB.png";
const FALLBACK_TENANT = { name: "MQ Store", logo_url: FALLBACK_LOGO, primary_color: "#DC2626" };

const TenantContext = createContext(null);
export const useTenant = () => useContext(TenantContext);

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  const r = parseInt(m[1], 16) / 255, g = parseInt(m[2], 16) / 255, b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: s * 100, l: l * 100 };
}
const hslStr = ({ h, s, l }) => `${h.toFixed(0)} ${s.toFixed(0)}% ${l.toFixed(0)}%`;

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(FALLBACK_TENANT);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/tenant/config");
      // OJO: no forzar FALLBACK_LOGO aquí -- un logo_url vacío es una elección
      // válida ("Sin imagen" en Personaliza tu Tienda), no un dato faltante.
      // El respaldo solo debe aplicar si la petición falla por completo (abajo).
      setTenant({ ...FALLBACK_TENANT, ...data });
    } catch {
      setTenant(FALLBACK_TENANT);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const hsl = hexToHsl(tenant.primary_color);
    if (hsl) {
      const hover = { ...hsl, l: Math.max(hsl.l - 10, 0) };
      document.documentElement.style.setProperty("--primary", hslStr(hsl));
      document.documentElement.style.setProperty("--primary-hover", hslStr(hover));
      document.documentElement.style.setProperty("--ring", hslStr(hsl));
      document.documentElement.style.setProperty("--accent-foreground", hslStr(hsl));
    }
  }, [tenant.primary_color]);

  // El HTML estático (public/index.html) trae un título/ícono genéricos --
  // el mismo build sirve para cualquier tienda clonada, así que el nombre y
  // logo reales solo se conocen aquí, después de cargar /tenant/config.
  useEffect(() => {
    document.title = tenant.name || "Tienda Digital";
    const favicon = document.getElementById("favicon");
    if (favicon && tenant.logo_url) favicon.href = tenant.logo_url;
  }, [tenant.name, tenant.logo_url]);

  return (
    <TenantContext.Provider value={{ tenant, loading, refresh }}>
      {children}
    </TenantContext.Provider>
  );
}
